import { useCallback, useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { RETENCAO_ISS_NAO_RETIDO, TRIBUTACAO_ISS_TRIBUTAVEL } from '../../../../constants/fiscal'
import {
  getDadosEmissaoNfse,
  getEmissoresNfse,
  getNfse,
  postNfse,
} from '../../../../services/api/https/fiscal/nfse.http'
import { getServicosNfse } from '../../../../services/api/https/registration/servico-nfse.http'
import { getCities, getUfs } from '../../../../services/api/https/system/system.http'
import {
  erroFormularioNfse,
  montarPayloadNfse,
  resolverModeloDescricao,
  round2,
  toNum,
  tomadorDaNfse,
  tomadorDoCadastro,
  tomadorVazio,
} from './emitirNfse.util'

const formularioInicial = () => ({
  emissor: null,
  servico: null,
  tributacao_iss: TRIBUTACAO_ISS_TRIBUTAVEL,
  tipo_retencao_iss: RETENCAO_ISS_NAO_RETIDO,
  aliquota: '',
  data_competencia: DateTime.local().startOf('day').toJSDate(),
  municipio_prestacao: null,
  valores: {
    valor_servico: '',
    desconto_incondicionado: '',
    desconto_condicionado: '',
    valor_deducao: '',
  },
  descricao_servico: '',
  informacoes_complementares: '',
  tomador: tomadorVazio('empresa'),
  notas: [],
  veiculo_placa: null,
})

function municipioDoEmissor(emissor) {
  if (!emissor || !emissor.city_id) return null
  return { id: emissor.city_id, name: emissor.city_name, uf: emissor.city_uf }
}

// contexto: { origem: 'avulsa' } | { origem: 'rota', deliveries_ids, fleet_id, veiculo_placa } | { duplicar: id }
export default function useEmitirNfse({ visible, tpId, contexto, onError }) {
  const [form, setForm] = useState(formularioInicial)
  const [emissores, setEmissores] = useState([])
  const [servicos, setServicos] = useState([])
  const [ufs, setUfs] = useState([])
  const [frete, setFrete] = useState(null)
  const [avisoFrete, setAvisoFrete] = useState(null)
  const [carregando, setCarregando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const descricaoAutomatica = useRef('')
  const cacheCidades = useRef(new Map())
  const formRef = useRef(form)
  formRef.current = form

  const daRota = Boolean(contexto && contexto.origem === 'rota')

  const carregarCidades = useCallback(async ufId => {
    if (!ufId) return []
    if (cacheCidades.current.has(ufId)) return cacheCidades.current.get(ufId)
    const lista = await getCities(ufId)
    cacheCidades.current.set(ufId, lista)
    return lista
  }, [])

  const atualizar = useCallback(patch => setForm(atual => ({ ...atual, ...patch })), [])

  // Padrões do serviço (2.21): tributação, retenção, alíquota e descrição pelo modelo.
  // A descrição só é trocada se o usuário não a editou
  const aplicarServico = useCallback((servico, contextoDescricao) => {
    setForm(atual => {
      const descricao = servico
        ? resolverModeloDescricao(servico.modelo_descricao || servico.descricao, contextoDescricao)
        : ''
      const manterDescricao =
        atual.descricao_servico && atual.descricao_servico !== descricaoAutomatica.current
      if (!manterDescricao) descricaoAutomatica.current = descricao
      return {
        ...atual,
        servico,
        tributacao_iss: servico ? servico.tributacao_iss : TRIBUTACAO_ISS_TRIBUTAVEL,
        tipo_retencao_iss: servico ? servico.tipo_retencao_iss : RETENCAO_ISS_NAO_RETIDO,
        aliquota:
          servico && servico.aliquota_iss !== null && servico.aliquota_iss !== undefined
            ? String(servico.aliquota_iss)
            : '',
        descricao_servico: manterDescricao ? atual.descricao_servico : descricao,
      }
    })
  }, [])

  const contextoDescricao = useCallback(
    (estado = formRef.current) => ({
      notas: estado.notas.map(nota => nota.identifier),
      placa: (contexto && contexto.veiculo_placa) || estado.veiculo_placa,
      data: estado.data_competencia,
      empresa: estado.notas.length ? estado.notas[0].company_nome : '',
    }),
    [contexto],
  )

  useEffect(() => {
    if (!visible || !tpId) return undefined
    let ativo = true
    descricaoAutomatica.current = ''
    setForm(formularioInicial())
    setFrete(null)
    setAvisoFrete(null)

    const carregar = async () => {
      try {
        setCarregando(true)
        const [listaEmissores, listaServicos, listaUfs] = await Promise.all([
          getEmissoresNfse(tpId),
          getServicosNfse(tpId, true),
          ufs.length ? Promise.resolve(ufs) : getUfs(),
        ])
        if (!ativo) return
        setEmissores(listaEmissores)
        setServicos(listaServicos)
        setUfs(listaUfs)

        const emissorUnico = listaEmissores.length === 1 ? listaEmissores[0] : null
        let base = {
          ...formularioInicial(),
          emissor: emissorUnico,
          municipio_prestacao: municipioDoEmissor(emissorUnico),
        }
        let servico = null
        let deliveriesIds = daRota ? contexto.deliveries_ids : []

        if (contexto && contexto.duplicar) {
          const anterior = await getNfse(tpId, contexto.duplicar)
          if (!ativo) return
          const emissor = listaEmissores.find(item => item.id === anterior.emissor_id) || null
          servico = listaServicos.find(item => item.id === anterior.servico_nfse_id) || null
          deliveriesIds = anterior.deliveries_ids || []
          base = {
            ...base,
            emissor,
            servico,
            tributacao_iss: anterior.tributacao_iss,
            tipo_retencao_iss: anterior.tipo_retencao_iss,
            aliquota:
              anterior.aliquota_iss !== null && anterior.aliquota_iss !== undefined
                ? String(anterior.aliquota_iss)
                : '',
            data_competencia: DateTime.fromISO(anterior.data_competencia).toJSDate(),
            municipio_prestacao: {
              id: anterior.municipio_prestacao_id,
              name: anterior.municipio_prestacao_name,
              uf: anterior.municipio_prestacao_uf,
            },
            valores: {
              valor_servico: String(anterior.valor_servico),
              desconto_incondicionado: anterior.desconto_incondicionado
                ? String(anterior.desconto_incondicionado)
                : '',
              desconto_condicionado: anterior.desconto_condicionado
                ? String(anterior.desconto_condicionado)
                : '',
              valor_deducao: anterior.valor_deducao ? String(anterior.valor_deducao) : '',
            },
            descricao_servico: anterior.descricao_servico || '',
            informacoes_complementares: anterior.informacoes_complementares || '',
            tomador: tomadorDaNfse(anterior, listaUfs),
            veiculo_placa: anterior.veiculo_placa || null,
          }
          descricaoAutomatica.current = ''
        }

        if (deliveriesIds.length) {
          const dados = await getDadosEmissaoNfse(tpId, deliveriesIds)
          if (!ativo) return
          base = { ...base, notas: dados.notas }
          if (!(contexto && contexto.duplicar)) {
            if (dados.tomador) base.tomador = tomadorDoCadastro(dados.tomador, listaUfs)
            if (dados.municipio_prestacao) base.municipio_prestacao = dados.municipio_prestacao
            if (dados.frete)
              base.valores = { ...base.valores, valor_servico: String(dados.frete.valor) }
            servico = listaServicos.find(item => item.padrao_frete_municipal) || null
          }
          setFrete(dados.frete)
          setAvisoFrete(dados.aviso_frete)
        }

        setForm(base)
        if (servico && !(contexto && contexto.duplicar))
          aplicarServico(servico, contextoDescricao(base))
      } catch (error) {
        if (ativo) onError(error)
      } finally {
        if (ativo) setCarregando(false)
      }
    }
    carregar()
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, tpId])

  // Events
  const onEmissorChange = emissor => {
    atualizar({
      emissor,
      ...(daRota || (contexto && contexto.duplicar)
        ? {}
        : { municipio_prestacao: municipioDoEmissor(emissor) }),
    })
  }

  const onServicoChange = servico => aplicarServico(servico, contextoDescricao())

  // Notas e competência entram no modelo da descrição: refaz o texto se o usuário não o editou
  const comDescricaoAtualizada = (atual, novo) => {
    if (!atual.servico || atual.descricao_servico !== descricaoAutomatica.current) return novo
    const descricao = resolverModeloDescricao(
      atual.servico.modelo_descricao || atual.servico.descricao,
      contextoDescricao(novo),
    )
    descricaoAutomatica.current = descricao
    return { ...novo, descricao_servico: descricao }
  }

  // O valor sugerido acompanha as notas mantidas, se o usuário não tiver mexido nele
  const onRemoverNota = nota => {
    const restantes = form.notas.filter(item => item.id !== nota.id)
    let novoFrete = null
    if (frete && frete.notas) {
      const valor = round2(
        frete.notas
          .filter(item => restantes.some(restante => restante.id === item.delivery_id))
          .reduce((soma, item) => soma + item.valor, 0),
      )
      novoFrete = { ...frete, valor }
      setFrete(novoFrete)
    }
    setForm(atual => {
      const sugeridoIntocado =
        novoFrete && toNum(atual.valores.valor_servico) === round2(frete.valor)
      return comDescricaoAtualizada(atual, {
        ...atual,
        notas: restantes,
        veiculo_placa: restantes.length ? atual.veiculo_placa : null,
        valores: sugeridoIntocado
          ? { ...atual.valores, valor_servico: String(novoFrete.valor) }
          : atual.valores,
      })
    })
  }

  const onCompetenciaChange = data => {
    setForm(atual => comDescricaoAtualizada(atual, { ...atual, data_competencia: data }))
  }

  // Notas trazidas pela 11.1: dados, frete sugerido e tomador padrão vêm da API, como na rota
  const adicionarNotas = async ids => {
    const todas = Array.from(new Set([...form.notas.map(nota => nota.id), ...ids]))
    const dados = await getDadosEmissaoNfse(tpId, todas)
    const freteAnterior = frete
    setFrete(dados.frete)
    setAvisoFrete(dados.aviso_frete)
    setForm(atual => {
      const valorAtual = toNum(atual.valores.valor_servico)
      const sugeridoIntocado =
        valorAtual === null || Boolean(freteAnterior && valorAtual === round2(freteAnterior.valor))
      return comDescricaoAtualizada(atual, {
        ...atual,
        notas: dados.notas,
        veiculo_placa: dados.veiculo ? dados.veiculo.placa : null,
        tomador:
          !atual.tomador.cnpj_cpf && dados.tomador
            ? tomadorDoCadastro(dados.tomador, ufs)
            : atual.tomador,
        valores:
          sugeridoIntocado && dados.frete
            ? { ...atual.valores, valor_servico: String(dados.frete.valor) }
            : atual.valores,
      })
    })
  }

  const onValorChange = (campo, valor) =>
    setForm(atual => ({ ...atual, valores: { ...atual.valores, [campo]: valor } }))

  const emitir = async () => {
    const erro = erroFormularioNfse(form)
    if (erro) throw new Error(erro)
    if (daRota && !form.notas.length) throw new Error('Mantenha pelo menos uma nota na NFS-e')
    try {
      setEnviando(true)
      return await postNfse(tpId, montarPayloadNfse(form, contexto || {}))
    } finally {
      setEnviando(false)
    }
  }

  return {
    form,
    emissores,
    servicos,
    ufs,
    frete,
    avisoFrete,
    carregando,
    enviando,
    daRota,
    carregarCidades,
    atualizar,
    onEmissorChange,
    onServicoChange,
    onRemoverNota,
    adicionarNotas,
    onCompetenciaChange,
    onValorChange,
    emitir,
  }
}
